import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface UserOrganization {
  organization_id: string;
  organization_name: string;
  role: string;
  is_primary: boolean;
  is_current: boolean;
  is_complete: boolean;
  joined_at?: string;
}

export interface UserOrganizationsResponse {
  organizations: UserOrganization[];
  current_organization_id: string | null;
}

export interface SwitchOrganizationRequest {
  organization_id: string;
}

export interface SwitchOrganizationResponse {
  message: string;
  organization_id: string;
  organization_name: string;
  role: string;
}

export interface CreateOrganizationRequest {
  name: string;
  company_size: '1-10' | '11-50' | '51-200' | '201-500' | '501-1000' | '1000+';
  industry: 'technology' | 'finance' | 'healthcare' | 'retail' | 'manufacturing' | 'education' | 'legal' | 'real-estate' | 'other';
  country: string;
  set_as_current: boolean;
}

export interface CreateOrganizationResponse {
  message: string;
  organization_id: string;
  organization_name: string;
  is_current: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private readonly apiUrl = `${environment.apiUrl}/users/me/organizations`;
  
  private organizationsSubject = new BehaviorSubject<UserOrganization[]>([]);
  public organizations$ = this.organizationsSubject.asObservable();
  
  private currentOrgSubject = new BehaviorSubject<UserOrganization | null>(null);
  public currentOrganization$ = this.currentOrgSubject.asObservable();
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Load all organizations the user belongs to
   */
  loadOrganizations(): Observable<UserOrganizationsResponse> {
    this.loadingSubject.next(true);
    return this.http.get<UserOrganizationsResponse>(this.apiUrl).pipe(
      tap(response => {
        this.organizationsSubject.next(response.organizations);
        const current = response.organizations.find(o => o.is_current);
        this.currentOrgSubject.next(current || null);
        this.loadingSubject.next(false);
      })
    );
  }

  /**
   * Switch to a different organization
   */
  switchOrganization(organizationId: string): Observable<SwitchOrganizationResponse> {
    this.loadingSubject.next(true);
    return this.http.put<SwitchOrganizationResponse>(`${this.apiUrl}/switch`, {
      organization_id: organizationId
    }).pipe(
      tap(response => {
        // Update local state
        const orgs = this.organizationsSubject.value.map(o => ({
          ...o,
          is_current: o.organization_id === organizationId
        }));
        this.organizationsSubject.next(orgs);
        const current = orgs.find(o => o.is_current);
        this.currentOrgSubject.next(current || null);
        this.loadingSubject.next(false);
      })
    );
  }

  /**
   * Create a new organization
   */
  createOrganization(request: CreateOrganizationRequest): Observable<CreateOrganizationResponse> {
    this.loadingSubject.next(true);
    return this.http.post<CreateOrganizationResponse>(this.apiUrl, request).pipe(
      tap(() => {
        this.loadingSubject.next(false);
        // Reload organizations to include the new one
        this.loadOrganizations().subscribe();
      })
    );
  }

  /**
   * Check if user has multiple organizations
   */
  hasMultipleOrganizations(): boolean {
    return this.organizationsSubject.value.length > 1;
  }

  /**
   * Get current organization
   */
  getCurrentOrganization(): UserOrganization | null {
    return this.currentOrgSubject.value;
  }
}

